import React, { useState, useEffect } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import paths from 'app/paths'
import { useNavigate } from 'react-router-dom'
import { useSmartEntity } from 'contexts/smart-entity'
import { useCategories } from 'pages/documents/utilities'
import { getContentSources } from 'components/tiptap/extensions/variable'
import { sum } from 'utilities/array'
import {
    Aside, Sections, Section,
    Kicker, CloseButton, FileNameIcon, FakeLabel
} from '../../s'
import { H4 } from 'components/typography/heading'
import Fields from './fields'
import { Field } from './fields/s'
import { Label, ValueDisplay } from 'components/form/field/s'
import Clamp from 'components/clamp'
import WordList from 'components/word-list'
import Actions from './actions'
import Meta from 'components/meta'

const PreviewSmartTemplateDetailsEdit = ({ listTemplate, mode = 'view', viewContext = 'modal', slim = false, modal, salt, ...props }) => {
    const { formatMessage } = useIntl()

    const navigate = useNavigate()

    const {
        entity: template,
        hasFetched
    } = useSmartEntity()

    const [smartFieldsMeta, setSmartFieldsMeta] = useState(null)
    const [editing, setEditing] = useState(false)
    const [loading, setLoading] = useState(true)
    const [initialized, setInitialized] = useState(false)

    useEffect(() => {
        if(hasFetched && !initialized) {
            if(mode === 'edit') {
                setEditing(true)
            }

            setSmartFieldsMeta(getContentSources(template))
            setLoading(false)
            setInitialized(true)
        }
    }, [hasFetched, mode, template, initialized])

    const {
        categories = [],
        getCategoryLabel
    } = useCategories()

    const NameAndCategory = ({ name, category }) => (
        <>
            <FileNameIcon
                name={name}
                displayName={name}
                extension="smart-template"
                ellipsify={false} />
            <FakeLabel>
                {getCategoryLabel(category ?? 'none')}
            </FakeLabel>
        </>
    )

    const MetaSection = ({ providedBy, updatedBy, updatedAt, createdBy, createdAt }) => {
        if((!providedBy && !updatedBy) || !updatedAt) {
            return null
        }

        return (
            <Section>
                <Meta
                    type="updated"
                    by={providedBy?.name ?? updatedBy}
                    at={updatedAt}
                    {...(providedBy ? { link: false } : null)}
                    time="fixed"
                    className="compact" />
                {(createdBy && createdAt) && (
                    <Meta
                        type="created"
                        by={createdBy}
                        at={createdAt}
                        time="fixed" />
                )}
            </Section>
        )
    }

    if(loading) {
        return (
            <Aside>
                <Sections>
                    <Section>
                        {(viewContext === 'modal') && (
                            <Kicker>
                                <H4 className="compact">
                                    <FormattedMessage
                                        id="documents_smart_template_details_heading"
                                        defaultMessage="Template details" />
                                </H4>
                                <CloseButton onClick={modal.dismiss} />
                            </Kicker>
                        )}
                        {!!listTemplate && (
                            <>
                                <NameAndCategory {...listTemplate} />
                                <Fields
                                    template={listTemplate}
                                    editing={false}
                                    categories={categories}
                                    salt={salt} />
                            </>
                        )}
                    </Section>
                    {!!listTemplate && (
                        <Section>
                            <Actions
                                {...props}
                                listTemplate={listTemplate}
                                slim={slim}
                                viewContext={viewContext}
                                setEditing={setEditing}
                                dismiss={(viewContext === 'modal') ?
                                    modal?.dismiss
                                    : () => navigate(paths.documents.templates)
                                }
                                salt={salt} />
                        </Section>
                    )}
                    {!!listTemplate && <MetaSection {...listTemplate} />}
                </Sections>
            </Aside>
        )
    }

    return (
        <Aside>
            <Sections>
                <Section>
                    {(viewContext === 'modal') && (
                        <Kicker>
                            <H4 className="compact">
                                <FormattedMessage
                                    id="documents_smart_template_details_heading"
                                    defaultMessage="Template details" />
                            </H4>
                            {!editing && <CloseButton onClick={modal.dismiss} />}
                        </Kicker>
                    )}
                    {!editing && (
                        <>
                            <NameAndCategory {...template} />
                            {template.description && (
                                <Field>
                                    <Label>
                                        <FormattedMessage
                                            id="noun_description"
                                            defaultMessage="Description" />
                                    </Label>
                                    <ValueDisplay>
                                        <Clamp
                                            className="compact"
                                            lines={3}>
                                            {template.description}
                                        </Clamp>
                                    </ValueDisplay>
                                </Field>
                            )}
                        </>
                    )}
                    <Fields
                        template={template}
                        editing={editing}
                        setEditing={setEditing}
                        categories={categories}
                        salt={salt} />
                    {(!!smartFieldsMeta?.length && !editing) && (
                        <Field className="compact">
                            <Label>
                                <FormattedMessage
                                    id="documents_smart_fields"
                                    defaultMessage="Smart fields" />
                            </Label>
                            <ValueDisplay>
                                <WordList
                                    words={[
                                        formatMessage({
                                            id: 'documents_smart_data_sources_count',
                                            defaultMessage: '{count, plural, one {1 data source} other {{count} data sources}}'
                                        }, { count: smartFieldsMeta.length }),
                                        formatMessage({
                                            id: 'documents_smart_variable_references_count',
                                            defaultMessage: '{count, plural, one {1 field reference} other {{count} field references}}'
                                        }, { count: sum(smartFieldsMeta.map(({ count }) => count)) })
                                    ]}
                                    className="compact" />
                            </ValueDisplay>
                        </Field>
                    )}
                </Section>
                {!editing && (
                    <>
                        <Section>
                            <Actions
                                {...props}
                                slim={slim}
                                viewContext={viewContext}
                                setEditing={setEditing}
                                dismiss={viewContext === 'modal' ?
                                    modal?.dismiss
                                    : () => navigate(paths.documents.templates)
                                }
                                salt={salt} />
                        </Section>
                        <MetaSection {...template} />
                    </>
                )}
            </Sections>
        </Aside>
    )
}

export default PreviewSmartTemplateDetailsEdit